// DesignSync User Guide v3 — Main Documentation Content
// This file contains the full structured user guide rendered in the Main Documentation modal.

export const USER_GUIDE_TITLE = 'DesignSync User Guide';

export const USER_GUIDE_SECTIONS = [
  { id: 'welcome', num: '01', title: 'Welcome — What DesignSync Does for You' },
  { id: 'tokens', num: '02', title: 'Understanding Design Tokens' },
  { id: 'dtcg', num: '03', title: 'The W3C DTCG Standard — The Universal Language' },
  { id: 'style-dictionary', num: '04', title: 'Style Dictionary — One Source, Every Platform' },
  { id: 'shopify', num: '05', title: 'Connecting to Your Shopify Store' },
  { id: 'github', num: '06', title: 'Your GitHub Repository — Your Token Home' },
  { id: 'pipeline', num: '07', title: 'The DesignSync Pipeline — Automated & Real-Time' },
  { id: 'portability', num: '08', title: 'Multi-Platform Portability' },
  { id: 'design-tools', num: '09', title: 'Using Your Tokens in Figma, Penpot, Framer & More' },
  { id: 'audit', num: '10', title: 'The Token Audit — Your Built-In Quality Check' },
  { id: 'manual-audit', num: '11', title: 'Your Manual Audit Option — Full Control When You Want It' },
  { id: 'ai', num: '12', title: 'Where AI Works for You' },
  { id: 'complete-flow', num: '13', title: 'The Complete Flow — From Shopify to Every Platform' },
  { id: 'figma-workflow', num: '14', title: 'Design Your Store in Figma — The Professional Workflow' },
];

export const USER_GUIDE_CONTENT = {
  welcome: `DesignSync Agent is your automated bridge between your Shopify store and your entire design system. It extracts your design values, structures them as industry-standard tokens, builds platform-ready outputs for every target, and deploys to your store — all with a single trigger and a human approval step before anything goes live.

<strong>What you can do with DesignSync:</strong>
<ul>
<li>Pull your Shopify theme's current design values and turn them into a professional design token system in minutes</li>
<li>Get platform-ready output files for web, iOS, Android, Tailwind, and more — all from the same source, all at once</li>
<li>Import your tokens directly into Figma, Penpot, Framer, or any design tool that reads the W3C DTCG standard</li>
<li>Review every proposed change in a GitHub Pull Request before it touches your live store</li>
<li>Audit token quality automatically on every run — and manually whenever you choose</li>
<li>Keep your design tool and your live Shopify storefront in sync — in both directions</li>
</ul>

<strong>What you never have to do:</strong>
<ul>
<li>Manually copy hex values between tools</li>
<li>Maintain separate CSS, Swift, XML, and JSON files by hand</li>
<li>Worry about inconsistencies between your Figma file and your live store</li>
</ul>`,

  tokens: `A design token stores a single design value — a color, a spacing unit, a font size — with a meaningful name. Instead of storing <code>#2E86AB</code> in six places across your codebase, you store it once as <code>color.brand.primary</code> and reference that name everywhere.

<strong>The three naming layers DesignSync generates for you:</strong>

<table>
<thead><tr><th>Layer</th><th>Example Name</th><th>Example Value</th><th>What It Represents</th></tr></thead>
<tbody>
<tr><td>Primitive</td><td>color.blue.500</td><td>#2E86AB</td><td>Raw named values — the full palette range</td></tr>
<tr><td>Semantic</td><td>color.action.primary</td><td>→ color.blue.500</td><td>Intent names — what the color is for</td></tr>
<tr><td>Component</td><td>button.primary.bg</td><td>→ color.action.primary</td><td>Component-specific overrides when needed</td></tr>
</tbody>
</table>

DesignSync generates all three layers automatically from your Shopify store.`,

  dtcg: `The W3C Design Token Community Group (DTCG) specification is an open standard developed collaboratively by Figma, Adobe, Shopify, Google, and the broader design tooling community. Any tool that implements this standard can read your tokens natively. You define your design decisions once — every tool understands them.

<strong>Every token DesignSync generates follows this format:</strong>

<pre><code>{
  "color.brand.primary": {
    "$value": "#2E86AB",
    "$type": "color",
    "$description": "Primary brand color — CTAs and active links"
  }
}</code></pre>

Your tokens are not locked into DesignSync or any single tool. Import directly into Figma via Tokens Studio, Penpot, Framer, Supernova, or any DTCG-compatible tool. Developers use it in React, Vue, Swift, Kotlin, or any framework.`,

  'style-dictionary': `Style Dictionary runs automatically in Stage 4 of the pipeline. It takes your single DTCG token source and transforms it simultaneously into every platform format you need.

<table>
<thead><tr><th>Output File</th><th>Target</th><th>What You Do With It</th></tr></thead>
<tbody>
<tr><td>tokens.css</td><td>Web / Shopify</td><td>Automatically deployed to your Shopify theme</td></tr>
<tr><td>tokens.dark.css</td><td>Web Dark Mode</td><td>Drop alongside tokens.css for instant dark mode</td></tr>
<tr><td>tailwind.preset.js</td><td>Tailwind CSS</td><td>Import into any Tailwind project</td></tr>
<tr><td>tokens.flat.json</td><td>Figma / Penpot / Framer / Any DTCG Tool</td><td>Import directly into any compatible design tool</td></tr>
<tr><td>tokens.android.xml</td><td>Android</td><td>Ready for Android Studio</td></tr>
<tr><td>tokens.ios.swift</td><td>iOS / Swift</td><td>Typed constants for Xcode</td></tr>
<tr><td>tokens.js (ESM)</td><td>JavaScript / TypeScript</td><td>Ready for React, Vue, Next.js, Remix</td></tr>
</tbody>
</table>`,

  shopify: `DesignSync connects via the Shopify Admin API — the same secure API used by Shopify's own apps.

<table>
<thead><tr><th>Setting</th><th>What It Is</th><th>Where to Find It</th></tr></thead>
<tbody>
<tr><td>Store Domain</td><td>Your Shopify domain</td><td>your-store.myshopify.com</td></tr>
<tr><td>Admin API Token</td><td>Private token with theme read/write access</td><td>Shopify Admin → Apps → Develop Apps → API credentials</td></tr>
<tr><td>Theme ID</td><td>Your published theme ID</td><td>Auto-detected by DesignSync — you can leave this blank</td></tr>
</tbody>
</table>

DesignSync reads your theme's compiled CSS, Liquid settings, and live rendered storefront. It writes exactly one file after your approval: <code>assets/tokens.css</code>. Nothing else in your theme is ever modified.`,

  github: `Your GitHub token repository is your design system's source of truth — versioned, auditable, and shareable with every tool and team member.

<table>
<thead><tr><th>File or Folder</th><th>What It Contains</th></tr></thead>
<tbody>
<tr><td>tokens/core.json</td><td>Your DTCG token source</td></tr>
<tr><td>build/css/tokens.css</td><td>Deployed to your Shopify theme</td></tr>
<tr><td>build/json/tokens.flat.json</td><td>Importable into Figma, Penpot, Framer</td></tr>
<tr><td>build/js/tokens.js</td><td>JavaScript ES module</td></tr>
<tr><td>build/ios/tokens.ios.swift</td><td>Swift constants for iOS</td></tr>
<tr><td>build/android/tokens.android.xml</td><td>Android resource values</td></tr>
</tbody>
</table>

DesignSync never commits directly to your main branch. Every set of proposed changes arrives as a Pull Request on a feature branch: <code>design-system/update-{timestamp}</code>. You review, you approve, you merge — and only then does Stage 6 deploy.`,

  pipeline: `<table>
<thead><tr><th>Stage</th><th>Name</th><th>What Happens For You</th></tr></thead>
<tbody>
<tr><td>1</td><td>Pull Shopify Theme Assets</td><td>Securely reads your published theme's CSS, Liquid settings, and all stored design values</td></tr>
<tr><td>2</td><td>AI Visual Analysis</td><td>Your live storefront is crawled — colors, fonts, and spacing captured as a shopper actually sees them</td></tr>
<tr><td>3</td><td>Claude Sonnet 4.6 — Token Structuring</td><td>AI transforms raw extracted values into a professionally named, W3C DTCG-compliant token system</td></tr>
<tr><td>4</td><td>Style Dictionary Build</td><td>All platform outputs generated simultaneously</td></tr>
<tr><td>4.5</td><td>Automated Token Audit</td><td>Checks how many generated tokens are actually referenced in your theme before anything is committed</td></tr>
<tr><td>5</td><td>GitHub PR — Your Review</td><td>A Pull Request is created. You review the audit, inspect the diff, and decide whether to approve</td></tr>
<tr><td>6</td><td>Deploy to Shopify</td><td>The moment you merge the PR, tokens.css deploys to your Shopify theme instantly</td></tr>
</tbody>
</table>

If fewer than 5% of tokens changed since the last run, the pipeline exits cleanly without creating a Pull Request. Your repository stays clean and focused. Most DesignSync runs complete in under three minutes.`,

  portability: `One design decision controls your Shopify storefront, iOS app, Android app, React project, and Tailwind build — simultaneously, from a single source, with no duplication.

<strong>Supported platforms:</strong> Shopify Storefront, Web Applications (React, Vue, Next.js, Remix), Tailwind CSS, iOS / Swift, Android, Figma / Penpot / Framer, and any custom tool or CI pipeline via tokens.flat.json.

<strong>The Figma ↔ Shopify loop — both directions:</strong>

<table>
<thead><tr><th>Direction</th><th>Flow</th><th>What Triggers It</th></tr></thead>
<tbody>
<tr><td>Shopify → Figma</td><td>DesignSync extracts live store values → structures as DTCG → you import into Figma via Tokens Studio</td><td>Manual run or Shopify theme update</td></tr>
<tr><td>Figma → Shopify</td><td>Designer updates token in Figma → Tokens Studio pushes to GitHub → DesignSync detects merge → deploys to Shopify</td><td>Tokens Studio push to design-tokens branch</td></tr>
</tbody>
</table>`,

  'design-tools': `The file you work with: <code>build/json/tokens.flat.json</code> in your GitHub repository — always up to date with the latest approved token values.

<strong>Figma — via Tokens Studio:</strong>
<ol>
<li>Install the Tokens Studio plugin from the Figma Community (free)</li>
<li>In Tokens Studio, go to Settings → Sync Provider → GitHub</li>
<li>Connect directly to your DesignSync token repository</li>
<li>Tokens Studio pulls your latest tokens from GitHub automatically</li>
<li>Apply tokens to your Figma components using the Tokens Studio panel</li>
<li>When you push updated tokens from Figma via Tokens Studio, DesignSync picks up the change and re-runs the pipeline</li>
</ol>

<strong>Penpot — native DTCG import:</strong>
<ol>
<li>Download tokens.flat.json from your GitHub repository</li>
<li>Open your Penpot project and go to the Design Tokens panel</li>
<li>Click Import and select your downloaded tokens.flat.json</li>
</ol>

<strong>Framer:</strong> Download tokens.flat.json and use Framer's variable import to make your tokens available as Framer variables across your project.`,

  audit: `Every DesignSync pipeline run includes an automatic token audit running between Stage 4 (build) and Stage 5 (GitHub PR).

<table>
<thead><tr><th>Measurement</th><th>Definition</th></tr></thead>
<tbody>
<tr><td>Declared</td><td>All tokens that exist in the DTCG source JSON Claude generated</td></tr>
<tr><td>Emitted</td><td>All CSS custom properties present in the generated tokens.css file</td></tr>
<tr><td>Referenced</td><td>All tokens actually called via <code>var(--token-name)</code> in your Shopify theme's CSS</td></tr>
<tr><td>Unused</td><td>Emitted but never referenced — tokens adding file size without being used</td></tr>
</tbody>
</table>

<table>
<thead><tr><th>Result</th><th>Threshold</th><th>What Happens</th></tr></thead>
<tbody>
<tr><td>Pass</td><td>&lt; 10% unused</td><td>The PR opens normally with a clean audit summary</td></tr>
<tr><td>Warn</td><td>10–25% unused</td><td>The PR opens with an expanded audit block listing unused token names</td></tr>
<tr><td>Block</td><td>&gt; 25% unused</td><td>Pipeline pauses before creating a PR. Your activity log shows the exact percentage</td></tr>
</tbody>
</table>

<strong>Common reasons for unused tokens:</strong> hover/focus state tokens, dark mode tokens not yet active, full spacing scale tokens where only part of the scale is used, and tokens for components not yet built. All of these are normal and safe to keep.`,

  'manual-audit': `Manual review is your option — not your obligation. The automated audit already protects your pipeline on every run.

<strong>When manual review adds the most value:</strong> after a large theme redesign, before a major product launch, when onboarding your iOS or Android developer, when you want to curate the token set, or when the automated audit returns a Block result.

<strong>Manual review workflow:</strong>
<ol>
<li>Open the Pull Request that DesignSync created in your GitHub repository</li>
<li>Review the Files Changed tab — every modified token value shown as a diff</li>
<li>Read the Token Audit summary in the PR body</li>
<li>If you want to edit before merging: download tokens/core.json from the PR branch, edit in your preferred tool, commit back to the same PR branch</li>
<li>Re-run the pipeline on the updated branch to regenerate all platform outputs with your edits</li>
<li>When satisfied, merge the PR — Stage 6 deploys your reviewed tokens to Shopify</li>
</ol>

<strong>If the audit returns a Block result:</strong> Check your pipeline activity log for the unused percentage and full list. If tokens are intentional, adjust the threshold in your pipeline settings. If tokens are not needed, edit tokens/core.json to remove them, then push to trigger a fresh run.`,

  ai: `DesignSync uses Claude Sonnet 4.6 (Anthropic) for a specific, bounded role.

<strong>Stage 2:</strong> A live crawl captures rendered design values — the colors, fonts, and spacing that appear to a shopper in their browser.

<strong>Stage 3 — the primary AI stage:</strong> Claude receives raw extracted values and transforms them into a professionally structured, W3C DTCG-compliant design token JSON. Specifically, Claude identifies semantic meaning, consolidates duplicates, assigns correct DTCG types, builds the three-layer hierarchy, writes descriptions, and validates its own output — retrying automatically until DTCG validation passes.

Every token Claude generates reflects a value that actually exists in your Shopify store. Claude does not invent colors, sizes, or values.

<strong>What AI never does:</strong>
<ul>
<li>AI never deploys to your store — deployment only happens when you merge a Pull Request</li>
<li>AI never modifies your theme code — only tokens.css is ever written, and only after your approval</li>
<li>AI never makes brand decisions for you — naming follows W3C DTCG conventions</li>
<li>AI never runs without a complete log — every prompt and response is recorded</li>
</ul>`,

  'complete-flow': `<strong>Flow A — Shopify-First</strong> (extract your live store into a complete design system):

Trigger a run → Stage 1: Pull Shopify Theme → Stage 2: AI Visual Analysis → Stage 3: Claude Sonnet 4.6 Token Structuring → Stage 4: Style Dictionary Build → Stage 4.5: Token Audit (Pass/Warn → continue · Block → pause) → Token diff > 5%? Yes → Stage 5: GitHub PR (you review, optionally import into Figma, approve and merge) → Stage 6: tokens.css deployed to Shopify · No → Clean exit, no PR.

<strong>Flow B — Design-First (Figma → Tokens Studio → DesignSync):</strong>

Designer updates token in Figma → Tokens Studio pushes DTCG JSON to GitHub → DesignSync detects branch merge → Stage 4: Style Dictionary builds all platform outputs simultaneously → Stage 4.5: Token Audit → Stage 5: GitHub PR — you review and approve → Stage 6: Deploy to Shopify and all registered targets.

In Flow B, Claude is not involved because your DTCG token source already exists — your designers created it in Figma. DesignSync acts as the distribution and quality layer.

Nothing reaches your live Shopify store without your explicit approval. The pipeline handles everything that is repetitive, error-prone, and time-consuming. The design decisions, the brand direction, and the final approval always belong to you.`,

  'figma-workflow': `This section describes how to combine Shopify CLI, Builder.io Visual Copilot, Tokens Studio, and DesignSync into a single professional design environment.

<strong>Step 1 — Pull your live store into Figma as editable frames:</strong>

<table>
<thead><tr><th>Tool</th><th>What It Does</th><th>Where to Get It</th></tr></thead>
<tbody>
<tr><td>Shopify CLI</td><td>Pulls your theme code to your local machine</td><td>Shopify CLI documentation</td></tr>
<tr><td>Builder.io Chrome Extension</td><td>Captures any live webpage as copyable layout data</td><td>Chrome Web Store → search Builder.io</td></tr>
<tr><td>Builder.io Visual Copilot (Figma)</td><td>Converts the captured layout into fully editable Figma frames</td><td>Figma Community → search Visual Copilot</td></tr>
</tbody>
</table>

<strong>Step 2 — Serve your theme locally:</strong>

<pre><code>npm install -g @shopify/cli @shopify/theme
shopify theme pull --store your-store.myshopify.com
shopify theme dev --store your-store.myshopify.com</code></pre>

<strong>Step 3 — Import your store into Figma:</strong> With your theme running on localhost, open the Builder.io Chrome extension, capture it, and paste into the Visual Copilot plugin. Your store becomes a fully editable Figma file within seconds.

<strong>Step 4 — Connect your DesignSync tokens via Tokens Studio:</strong>
<ol>
<li>Install Tokens Studio in Figma (free plugin)</li>
<li>In Tokens Studio: Settings → Sync → GitHub</li>
<li>Point it to your design-tokens repository and tokens/core.json</li>
<li>Click Pull from GitHub — your brand tokens appear immediately</li>
<li>Apply tokens to your imported frames</li>
</ol>

<strong>Step 5 — Design, iterate, and approve:</strong> With your live store in Figma and real tokens applied, design professionally — changing layouts, testing color combinations, trying different spacing scales — all within your actual brand system. Share a Figma link with stakeholders for feedback before anything touches production.

<strong>Step 6 — Push approved changes back to Shopify:</strong> When design decisions are finalized, Tokens Studio pushes updated token values to GitHub. DesignSync detects the change and runs the full pipeline including Token Audit, generates all platform outputs, opens a PR for your approval, and deploys to Shopify on merge.`,
};
